package ai.wagemix.web.screens.account

import ai.wagemix.logic.account.WMProfileNameProblems
import ai.wagemix.web.theme.WMInlineGapVar
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.components.text.SpanText
import ai.wagemix.web.theme.WMCaptionTextStyle
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.DisplayStyle
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.account.WMAccountPresenter
import ai.wagemix.logic.account.WMAccountPresenter.Action
import ai.wagemix.logic.account.WMAccountPresenter.Event
import ai.wagemix.logic.account.WMAccountPresenter.Prompt
import ai.wagemix.logic.account.WMDefaultAccountPresenter
import ai.wagemix.logic.account.WMProfile
import ai.wagemix.logic.jobdescriptions.WMUploadQuota
import ai.wagemix.net.models.WMSubscriptionInfo
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.web.components.WMBadge
import ai.wagemix.web.components.WMBadgeKind
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMFieldRow
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMKeyValue
import ai.wagemix.web.components.WMMeter
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMPanel
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.theme.WMAppContentGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.px

interface WMAccountWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val profileState: State<WMProfile?>
    val saveAllowedState: State<Boolean>
    val subscriptionState: State<WMSubscriptionInfo?>
    val quotaState: State<WMUploadQuota?>
    val resetLinkSentState: State<Boolean>
    /** Which names in the form are too long; `null` until the profile loads. */
    val nameProblemsState: State<WMProfileNameProblems?>
}

abstract class WMBaseAccountWebViewModel<P: Prompt, E: Event<P>, A: Action, PR: WMAccountPresenter<P, E, A>>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMAccountWebViewStateHolder<P> {

    override val profileState: State<WMProfile?> get() = _profileState
    protected val _profileState = mutableStateOf<WMProfile?>(null)

    override val saveAllowedState: State<Boolean> get() = _saveAllowedState
    protected val _saveAllowedState = mutableStateOf(false)

    override val subscriptionState: State<WMSubscriptionInfo?> get() = _subscriptionState
    protected val _subscriptionState = mutableStateOf<WMSubscriptionInfo?>(null)

    override val quotaState: State<WMUploadQuota?> get() = _quotaState
    protected val _quotaState = mutableStateOf<WMUploadQuota?>(null)

    override val resetLinkSentState: State<Boolean> get() = _resetLinkSentState
    protected val _resetLinkSentState = mutableStateOf(false)

    override val nameProblemsState: State<WMProfileNameProblems?> get() = _nameProblemsState
    protected val _nameProblemsState = mutableStateOf<WMProfileNameProblems?>(null)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            profileUpdate?.let { _profileState.value = it }
            saveAllowedUpdate?.let { _saveAllowedState.value = it }
            subscriptionUpdate?.let { _subscriptionState.value = it.value }
            quotaUpdate?.let { _quotaState.value = it }
            resetLinkSentUpdate?.let { _resetLinkSentState.value = it }
            nameProblemsUpdate?.let { _nameProblemsState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userEditedProfile(firstName: String, lastName: String) = presenter.userEditedProfile(firstName, lastName)
    fun userRequestedSaveProfile(firstName: String, lastName: String) = presenter.userRequestedSaveProfile(firstName, lastName)
    fun userRequestedPasswordReset() = presenter.userRequestedPasswordReset()
    fun userRequestedBillingPortal(returnPath: String?) = presenter.userRequestedBillingPortal(returnPath)
}

typealias WMDefaultAccountWebViewModel = WMBaseAccountWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultAccountPresenter>

fun createDefaultAccountWebViewModel(
    presenter: WMDefaultAccountPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultAccountWebViewModel = object: WMDefaultAccountWebViewModel(presenter, externalNavigation) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}

@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMAccountPresenter<P, E, A>, VM: WMBaseAccountWebViewModel<P, E, A, PR>> WMAccountScreen(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    /** The environment tag the billing portal returns to ("prod", "staging", "dev"). */
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        WMAccountScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            profileState = vm.profileState,
            saveAllowedState = vm.saveAllowedState,
            subscriptionState = vm.subscriptionState,
            quotaState = vm.quotaState,
            resetLinkSentState = vm.resetLinkSentState,
            nameProblemsState = vm.nameProblemsState,
            modifier = modifier,
            onProfileEdit = vm::userEditedProfile,
            onProfileSave = vm::userRequestedSaveProfile,
            onPasswordReset = vm::userRequestedPasswordReset,
            onManageBilling = { vm.userRequestedBillingPortal(WMRoutes.ACCOUNT) }
        )
        val prompt = vm.promptState.value
        WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = { vm.userRespondedToPrompt(FSPrompt.Response(prompt, it)) }) { p ->
            when (p) {
                is Prompt.ProfileSaved -> WMDialog(
                    title = strings.account.profileSavedTitle,
                    description = strings.account.profileSavedMessage,
                    onDismiss = { vm.userRespondedToPrompt(FSPrompt.Response(prompt, 0)) },
                    actions = { WMButton(strings.common.close, modifier = WMDialogButtonStyle.toModifier()) { vm.userRespondedToPrompt(FSPrompt.Response(prompt, 0)) } }
                ) {}
                else -> Unit
            }
        }
    }
}

@Composable
fun WMAccountScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    profileState: State<WMProfile?>,
    saveAllowedState: State<Boolean>,
    subscriptionState: State<WMSubscriptionInfo?>,
    quotaState: State<WMUploadQuota?>,
    resetLinkSentState: State<Boolean>,
    nameProblemsState: State<WMProfileNameProblems?>,
    modifier: Modifier = Modifier,
    onProfileEdit: (String, String) -> Unit,
    onProfileSave: (String, String) -> Unit,
    onPasswordReset: () -> Unit,
    onManageBilling: () -> Unit
) {
    val a = strings.account
    val profile = profileState.value
    var firstName by remember(profile?.firstName) { mutableStateOf(profile?.firstName ?: "") }
    var lastName by remember(profile?.lastName) { mutableStateOf(profile?.lastName ?: "") }

    // Design deviation E1: Profile and Password on the left, Subscription on the right from Large up; one column below.
    Div(WMAccountColumnsStyle.toModifier().then(modifier).toAttrs()) {
        Column(modifier = Modifier.fillMaxWidth().gap(WMAppContentGapVar.value())) {
            WMPanel(title = a.profile) {
                WMFieldRow {
                    val names = nameProblemsState.value
                    val firstError = names?.firstNameLength?.let { a.firstNameTooLong(names.limit, it) }
                    val lastError = names?.lastNameLength?.let { a.lastNameTooLong(names.limit, it) }
                    WMField(a.firstName, errorText = firstError) {
                        WMTextField(firstName, onTextChange = { firstName = it; onProfileEdit(it, lastName) })
                    }
                    WMField(a.lastName, errorText = lastError) {
                        WMTextField(lastName, onTextChange = { lastName = it; onProfileEdit(firstName, it) })
                    }
                }
                WMField(a.email) {
                    Row(modifier = Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                        WMTextField(profile?.email ?: "", onTextChange = {}, readOnly = true, modifier = Modifier.fillMaxWidth())
                        if (profile != null) {
                            val verified = profile.emailVerified
                            WMBadge(if (verified) a.verified else a.unverified, kind = if (verified) WMBadgeKind.Success else WMBadgeKind.Warning)
                        }
                    }
                }
                WMButton(a.saveProfile, enabled = saveAllowedState.value && !loadingState.value) { onProfileSave(firstName, lastName) }
            }

            WMPanel(title = a.password) {
                if (resetLinkSentState.value) {
                    WMNotice(a.resetLinkSent, kind = WMNoticeKind.Info)
                } else {
                    WMButton(
                        a.sendResetLink,
                        kind = WMButtonKind.Secondary,
                        enabled = !loadingState.value,
                        leadingIcon = WMIconGlyph.Mail,
                        onClick = onPasswordReset
                    )
                }
            }

        }
        WMPanel(title = a.subscription) {
            val sub = subscriptionState.value
            val plan = sub?.plan ?: WMSubscriptionType.None
            WMKeyValue(a.plan, when (plan) { WMSubscriptionType.None -> strings.common.free; WMSubscriptionType.Basic -> strings.common.basic; WMSubscriptionType.Pro -> strings.common.pro })
            if (plan == WMSubscriptionType.None) {
                WMNotice(a.noSubscription, kind = WMNoticeKind.Info)
            } else {
                sub?.currentPeriodEnd?.let { end ->
                    val date = end.toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
                    WMKeyValue(if (sub.cancelAtPeriodEnd) a.endsOn else a.renews, listOfNotNull(date, sub.interval).joinToString(" · "))
                }
            }
            quotaState.value?.let { q ->
                val qt = q.quota
                if (qt != null && qt > 0) {
                    WMKeyValue(a.uploads, "${q.used} / $qt")
                    WMMeter(fraction = q.used.toDouble() / qt, accessibilityString = "${q.used} / $qt", warning = q.exhausted)
                } else if (qt == null) {
                    WMKeyValue(a.uploads, strings.jobDescriptions.quotaUnlimited)
                }
            }
            // Design deviation E2: the upgrade path and billing on the card, and who handles the payment.
            Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                when (plan) {
                    WMSubscriptionType.None -> WMButton(strings.common.viewPlans) { kotlinx.browser.window.location.href = WMRoutes.PRICING }
                    WMSubscriptionType.Basic -> WMButton(strings.common.upgradeToPro) { kotlinx.browser.window.location.href = WMRoutes.PRICING }
                    WMSubscriptionType.Pro -> Unit
                }
                if (plan != WMSubscriptionType.None) {
                    WMButton(
                        a.manageBilling,
                        kind = WMButtonKind.Secondary,
                        enabled = !loadingState.value,
                        leadingIcon = WMIconGlyph.CreditCard,
                        onClick = onManageBilling
                    )
                }
            }
            if (plan != WMSubscriptionType.None) {
                SpanText(a.billingByStripe, WMCaptionTextStyle.toModifier().color(ColorMode.current.toPalette().fs.onSurfaceVariant))
            }
        }
    }
}

/** The account page's columns (design deviation E1): one below Large, then two equal columns. */
val WMAccountColumnsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(WMAppContentGapVar.value())
            .alignItems(AlignItems.Start)
    }
    FSBreakpoint.Large { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
}
